// AraAra — Collection Spotlight (coverflow).
// Centre card enlarged + full-colour; neighbours scaled down + dimmed.
// Click a card to focus it; drag / wheel / arrow keys / swipe to rotate.
// Progressive enhancement: with no JS the cards are a normal snap-scroll row.
class CollectionSpotlight extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-spotlight-track]');
    this.cards = Array.from(this.querySelectorAll('[data-spotlight-card]'));
    if (!this.track || this.cards.length === 0) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.activeIndex = Math.floor(this.cards.length / 2);

    this._onScroll = this._rafThrottle(() => this._updateActive());
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    this.cards.forEach((card, i) => {
      card.addEventListener('click', (e) => {
        if (i !== this.activeIndex) {
          e.preventDefault();
          this.focusIndex(i);
        }
      });
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.focusIndex(this.activeIndex + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.focusIndex(this.activeIndex - 1); }
    });

    this._enableDrag();

    // Centre the initial active card without animation.
    requestAnimationFrame(() => {
      this._scrollToIndex(this.activeIndex, 'auto');
      this._updateActive();
    });
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this._onScroll);
  }

  focusIndex(index) {
    const clamped = Math.max(0, Math.min(this.cards.length - 1, index));
    this._scrollToIndex(clamped, this.reduceMotion ? 'auto' : 'smooth');
  }

  _scrollToIndex(index, behavior) {
    const card = this.cards[index];
    if (!card) return;
    const left = card.offsetLeft - (this.track.clientWidth - card.clientWidth) / 2;
    this.track.scrollTo({ left, behavior });
  }

  _updateActive() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    this.cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const dist = Math.abs(cardCenter - center);
      if (dist < bestDist) { bestDist = dist; best = i; }
      // Distance-based falloff drives scale/opacity for a smooth coverflow.
      const norm = Math.min(1, dist / (card.clientWidth * 1.4));
      card.style.setProperty('--falloff', norm.toFixed(3));
    });
    if (best !== this.activeIndex) {
      this.cards[this.activeIndex]?.removeAttribute('data-active');
      this.activeIndex = best;
      this.cards[best]?.setAttribute('data-active', '');
    }
  }

  _enableDrag() {
    let down = false, startX = 0, startScroll = 0, moved = false;
    const start = (x) => { down = true; moved = false; startX = x; startScroll = this.track.scrollLeft; };
    const move = (x) => {
      if (!down) return;
      const dx = x - startX;
      if (Math.abs(dx) > 4) moved = true;
      this.track.scrollLeft = startScroll - dx;
    };
    const end = () => {
      if (!down) return;
      down = false;
      if (moved && !this.reduceMotion) this.focusIndex(this.activeIndex);
    };
    this.track.addEventListener('pointerdown', (e) => { start(e.clientX); });
    this.track.addEventListener('pointermove', (e) => { move(e.clientX); });
    window.addEventListener('pointerup', end);
    // Block click-through after a drag.
    this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

  _rafThrottle(fn) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { fn(); ticking = false; });
    };
  }
}

if (!customElements.get('collection-spotlight')) {
  customElements.define('collection-spotlight', CollectionSpotlight);
}
